import produce from 'immer';
import { combineReducers } from 'redux';
import {
  ProcessAction,
  ProcessActionType,
  ProcessEditingDetails,
} from '../../actions/processActions';

const loading = (state = false, action: ProcessAction) => {
  switch (action.type) {
    case ProcessActionType.GET_PROCESSES:
      return true;
    case ProcessActionType.GET_PROCESSES_OK:
    case ProcessActionType.GET_PROCESSES_ERROR:
      return false;
    default:
      return state;
  }
};

const loadingError = (state = '', action: ProcessAction) => {
  switch (action.type) {
    case ProcessActionType.GET_PROCESSES:
      return '';
    case ProcessActionType.GET_PROCESSES_ERROR:
      return action.error;
    default:
      return state;
  }
};

const creating = (state = false, action: ProcessAction) => {
  switch (action.type) {
    case ProcessActionType.CREATE_PROCESS:
      return true;
    case ProcessActionType.CREATE_PROCESS_OK:
    case ProcessActionType.CREATE_PROCESS_ERROR:
      return false;
    default:
      return state;
  }
};

const createError = (state = '', action: ProcessAction) => {
  switch (action.type) {
    case ProcessActionType.CREATE_PROCESS:
      return '';
    case ProcessActionType.CREATE_PROCESS_ERROR:
      return action.error;
    default:
      return state;
  }
};

type LoadingState = { loading: boolean; loadingError: string };
type DeletingState = { deleting: boolean; deleteError: string };
type StartingState = { starting: boolean; startingError: string };
type GeneratingSecret = {
  generatingSecret: boolean;
  generatingSecretError: string;
};
type DeletingSecret = { deletingSecret: boolean; deletingSecretError: string };
type EditingStates = {
  [editingStatusKey in ProcessEditingDetails['statusKey']]: boolean;
};
type EditingErrors = {
  [editingErrorKey in ProcessEditingDetails['errorKey']]: string;
};

export type ProcessState = LoadingState &
  DeletingState &
  EditingStates &
  EditingErrors &
  StartingState &
  GeneratingSecret &
  DeletingSecret;

interface ProcessStatesById {
  [id: string]: ProcessState;
}

const byId = (state: ProcessStatesById = {}, action: ProcessAction) =>
  produce(state, (draft) => {
    switch (action.type) {
      case ProcessActionType.GET_PROCESS: {
        draft[action.processId] = {
          ...draft[action.processId],
          loading: true,
          loadingError: '',
        };
        break;
      }
      case ProcessActionType.GET_PROCESS_ERROR: {
        draft[action.processId].loading = false;
        draft[action.processId].loadingError = action.error;
        break;
      }
      case ProcessActionType.GET_PROCESS_OK: {
        draft[action.processId].loading = false;
        break;
      }
      case ProcessActionType.EDIT_PROCESS: {
        draft[action.processId][action.statusKey] = true;
        draft[action.processId][action.errorKey] = '';
        break;
      }
      case ProcessActionType.EDIT_PROCESS_OK: {
        draft[action.processId][action.statusKey] = false;
        break;
      }
      case ProcessActionType.EDIT_PROCESS_ERROR: {
        draft[action.processId][action.statusKey] = false;
        draft[action.processId][action.errorKey] = action.error;
        break;
      }
      case ProcessActionType.DELETE_PROCESS: {
        draft[action.processId].deleting = true;
        draft[action.processId].deleteError = '';
        break;
      }
      case ProcessActionType.DELETE_PROCESS_OK: {
        draft[action.processId].deleting = false;
        break;
      }
      case ProcessActionType.DELETE_PROCESS_ERROR: {
        draft[action.processId].deleting = false;
        draft[action.processId].deleteError = action.error;
        break;
      }
      case ProcessActionType.DELETE_PROCESS_SECRET: {
        draft[action.processId].deletingSecret = true;
        draft[action.processId].deletingSecretError = '';
        break;
      }
      case ProcessActionType.DELETE_PROCESS_SECRET_OK: {
        draft[action.processId].deletingSecret = false;
        break;
      }
      case ProcessActionType.DELETE_PROCESS_SECRET_ERROR: {
        draft[action.processId].deletingSecret = false;
        draft[action.processId].deletingSecretError = action.error;
        break;
      }
      case ProcessActionType.GENERATE_PROCESS_SECRET: {
        draft[action.processId].generatingSecret = true;
        draft[action.processId].generatingSecretError = '';
        break;
      }
      case ProcessActionType.GENERATE_PROCESS_SECRET_OK: {
        draft[action.processId].generatingSecret = false;
        break;
      }
      case ProcessActionType.GENERATE_PROCESS_SECRET_ERROR: {
        draft[action.processId].generatingSecret = false;
        draft[action.processId].generatingSecretError = action.error;
        break;
      }
      case ProcessActionType.GET_PROCESSES_OK: {
        action.data.forEach((proc) => {
          draft[proc.id] = {} as ProcessState;
        });

        break;
      }
      case ProcessActionType.CREATE_PROCESS_OK: {
        draft[action.data.id] = {} as ProcessState;
        break;
      }
      default:
        return state;
    }
  });

export default combineReducers({
  loading,
  loadingError,
  creating,
  createError,
  byId,
});
